import React, { useState } from "react";
import PropTypes from "prop-types";

import { bandCount, bandIndices, bandLabel } from "./deck";

// The full deck, grouped into frequency bands you can switch on and off, so
// "the first few hundred are too easy, I just want 300-500" is one click each.
//
// Rows only render for the expanded band - at a few thousand nouns, rendering
// every row up front is a visible pause on open.
export default function Browse({
  colors,
  colorCoding,
  known,
  nouns,
  onToggleBand,
  onSetBandKnown,
  onToggleKnown,
  bands,
}) {
  const [expanded, setExpanded] = useState(null);

  const knownWords = new Set(known);
  const enabled = new Set(bands);
  const total = nouns.length;

  return (
    <div className="fna-browse">
      <p className="fna-browse-intro">
        Switch bands on or off to choose what you practice. Bands are ordered by
        how common the words are, so band 1 is the most common French nouns.
      </p>

      {Array.from({ length: bandCount(total) }, (_, band) => {
        const indices = bandIndices(band, total);
        const knownInBand = indices.filter((i) => knownWords.has(nouns[i].word)).length;
        const isExpanded = expanded === band;

        return (
          <div className="fna-band" key={band}>
            <div className="fna-band-header">
              <label className="fna-band-toggle" htmlFor={`fna-band-${band}`}>
                <input
                  checked={enabled.has(band)}
                  id={`fna-band-${band}`}
                  onChange={() => onToggleBand(band)}
                  type="checkbox"
                />
                <span className="fna-band-label">{bandLabel(band, total)}</span>
              </label>

              <span className="fna-band-known">
                {knownInBand} / {indices.length} known
              </span>

              <div className="fna-band-actions">
                <button
                  className="btn btn-link btn-xs"
                  onClick={() => setExpanded(isExpanded ? null : band)}
                  type="button"
                >
                  {isExpanded ? "Hide" : "Show"} words
                </button>
                <button
                  className="btn btn-link btn-xs"
                  onClick={() => onSetBandKnown(band, knownInBand < indices.length)}
                  type="button"
                >
                  {knownInBand < indices.length ? "Mark all known" : "Clear known"}
                </button>
              </div>
            </div>

            {isExpanded && (
              <table className="fna-band-table">
                <tbody>
                  {indices.map((i) => {
                    const noun = nouns[i];
                    const isKnown = knownWords.has(noun.word);

                    return (
                      <tr className={isKnown ? "fna-row-known" : null} key={noun.word}>
                        <td className="fna-row-rank">{i + 1}</td>
                        <td
                          className="fna-row-word"
                          style={colorCoding ? { color: colors[noun.gender] } : null}
                        >
                          {noun.written}
                        </td>
                        <td className="fna-row-translations">
                          {noun.translations.join("; ")}
                        </td>
                        <td className="fna-row-action">
                          <button
                            className="btn btn-link btn-xs"
                            onClick={() => onToggleKnown(noun.word)}
                            type="button"
                          >
                            {isKnown ? "Practice again" : "Known"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
}

Browse.propTypes = {
  bands: PropTypes.arrayOf(PropTypes.number).isRequired,
  colorCoding: PropTypes.bool.isRequired,
  colors: PropTypes.shape({ m: PropTypes.string, f: PropTypes.string }).isRequired,
  known: PropTypes.arrayOf(PropTypes.string).isRequired,
  nouns: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSetBandKnown: PropTypes.func.isRequired,
  onToggleBand: PropTypes.func.isRequired,
  onToggleKnown: PropTypes.func.isRequired,
};
